import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Choice, QuestionType, VoyageVibeStateInput } from '@generated/graphql';
import { GET_QUESTIONS } from '@queries/questions';
import { GET_LAST_STEP } from '@queries/trip-profiles';
import QuestionnaireBoard, { ColumnData } from '@components/questionnaire-board';
import Breadcrumb from '@components/breadcrumb';
import NextNav from '@components/next-nav';
import ErrorPage from '@components/error-page';
import { FIND_VOYAGE_VIBE_STATES, UPSERT_VOYAGE_VIBE_STATES } from '@queries/voyage-vibe-state';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';

const columnData: ColumnData[] = [
  {
    key: '100',
    name: 'Pick List',
    items: [],
  },
  {
    key: '101',
    name: 'Selected',
    items: [],
  },
];

function VibeAttractions() {
  const { clientId = '', tripProfileId = '' } = useParams();
  const [numPicked, setNumPicked] = useState(0);
  const boardStateHook = useState(columnData);
  const [boardState, setBoardState] = boardStateHook;
  const apolloClient = useApolloClient();

  const allQuestions = apolloClient.readQuery({
    query: GET_QUESTIONS,
  });
  const question = allQuestions?.findQuestions.filter((q) => q.type === QuestionType.VibeAttr)[0];

  const { loading, error, data } = useQuery(FIND_VOYAGE_VIBE_STATES, {
    variables: { tripProfileId, specificQuestionIds: [question ? question.id : ''] },
    skip: !clientId || !tripProfileId || !question,
  });
  const [upsertVoyageVibeStates, mutationState] = useMutation(UPSERT_VOYAGE_VIBE_STATES, {
    refetchQueries: [
      { query: GET_LAST_STEP, variables: { id: tripProfileId } },
      {
        query: FIND_VOYAGE_VIBE_STATES,
        variables: { tripProfileId, specificQuestionIds: [question ? question.id : ''] },
      },
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    // load data into boardState, the pick list (from the question choices) and selected items (from db)
    if (data) {
      const { findVoyageVibeStates } = data;
      setBoardState((prevState) => {
        // Update boardState
        const newState = [...prevState];
        // need the existing id to update the  when saving.  This is a hack, as the id should be in the overall state.
        newState[1].id = findVoyageVibeStates[0]?.id;

        // Extract choices with id from findVoyageVibeStates.rank
        const newItems: Choice[] =
          question?.choices.filter((choice) => findVoyageVibeStates[0]?.rank?.includes(choice.id)) || [];
        newState[1].items = [...newItems];
        // Remove items from newState[0].items that are in newState[1].items
        const pickListItems = question?.choices.filter((choice) => !newState[1].items.includes(choice)) || [];
        newState[0].items = [...pickListItems];

        return newState;
      });
    }
  }, [data, question?.choices, setBoardState]);

  if (!clientId || !tripProfileId) return null;
  if (!question) {
    const msg = 'No VibeAttractions question found';
    Sentry.captureException(Error(msg), {
      extra: { questions: JSON.stringify(question, null, 2) },
    });
    return <ErrorPage message={msg} />;
  }
  if (loading || mutationState.loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;
  if (mutationState.error) return <GraphqlError error={mutationState.error} />;

  const save = async () => {
    const rank = boardState[1].items.map((item) => item.id);
    const vibeState: VoyageVibeStateInput = {
      id: boardState[1].id || undefined,
      tripProfileId,
      questionId: question.id,
      title: question.type,
      rank,
    };
    await upsertVoyageVibeStates({
      variables: {
        voyageVibeStates: [vibeState],
      },
    });
  };

  return (
    data && (
      <div>
        <Breadcrumb pageStep={4} />
        <QuestionnaireBoard
          title="Voyage Vibe - Attractions"
          question={question}
          setNumPicked={setNumPicked}
          stateHook={boardStateHook}
        />
        <NextNav
          next={`/clients/${clientId}/trip-profiles/${tripProfileId}/vibe-activities`}
          nextDisabled={numPicked < 5}
          saveFunction={save}
        />
      </div>
    )
  );
}

export default VibeAttractions;
